import * as t from 'io-ts';
import { isUndefined } from 'lodash-es';

import type { AutoDateDefinition } from 'header/types';

import { define, createSpecSettingsInterface } from './_common';
import { specSettingsInterface as dateRangeSettingsInterface } from './dateRangeSetting';

export interface AutoDateSettingDefinition extends AutoDateDefinition {
    key: 'AUTO_DATE';
}

const definition: AutoDateSettingDefinition = {
    type: 'AutoDate',
    key: 'AUTO_DATE',
    label: 'AutoDate',
    scope: 'designer',
    value: null,
};

const dateInputCodec = t.type({ offset: t.number });
const dateRangeInputCodec = t.type({ from: dateInputCodec, to: dateInputCodec });

const specSettingsInterface = createSpecSettingsInterface(
    definition,
    t.union([t.null, dateInputCodec, dateRangeInputCodec]),
);

export const codec = specSettingsInterface.input;

export const AutoDateSettingDefinition = define({
    key: 'AUTO_DATE',
    label: 'Autodate',
    definition,
    fromSetting: settings => {
        if (isUndefined(settings.dateType)) return undefined;

        const value = specSettingsInterface.extractValue(settings.autoDate);
        if (!value) return value;

        if (dateInputCodec.is(value)) {
            return { from: value, to: null };
        }
        const isRange = dateRangeSettingsInterface.extractValue(settings.dateRange);
        return {
            ...value,
            to: isRange ? value.to : null,
        };
    },
    createDefinitionWithOpts: (opts = {}) => ({ ...definition, ...opts }),
    createDefinitionFromSpec: settings =>
        specSettingsInterface.createDefinitionWithSettings(settings.autoDate),
    applySettingDependency: ({ diff, settings }) => {
        const autoDateValue = settings.AUTO_DATE;
        const dateRangeChanged = !!diff.DATE_RANGE;
        const autoDateChanged = !!diff.AUTO_DATE;

        if (!autoDateValue || (!dateRangeChanged && !autoDateChanged)) return undefined;

        const isRange = dateRangeSettingsInterface.extractValue(settings.DATE_RANGE);

        return {
            value: {
                ...autoDateValue,
                to: isRange ? autoDateValue?.to || { offset: 0 } : null,
            },
        };
    },
});
